import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import OnlyOfficeDocumentEditor from "components/lib/Shared/OnlyOfficeDocumentEditor";
import { classNames, getFullName } from "utils";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { updateTemplateState } from "state/slices/template";
import { updateNominalSharesSlice } from "state/slices/tables/nominalSharesTable";
import Button from "components/lib/Shared/Button";
import { Constants } from "config/constants";
import _uniq from "lodash/uniq";
import ExistingAuthorizationsTable from "components/PLCTABS/NominalShareCapitalTab/ExistingAuthorizationsTable";
import Cookies from "js-cookie";

function ShareRights({ tabs = [], toggle = () => null, activeTab, disabled }) {
  return (
    <div>
      <div className="sm:hidden">
        <label htmlFor="tabs" className="sr-only">
          Select a tab
        </label>
      </div>
      <div className="hidden sm:block">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <Button
                key={tab}
                disabled={disabled}
                overrideBaseStyles
                className={classNames(
                  tab === activeTab
                    ? "border-sflPrimary text-sflPrimary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                  "cursor-pointer whitespace-nowrap py-1 px-1 border-b-2 font-medium text-sm disabled:cursor-not-allowed"
                )}
                onClick={() => (disabled ? null : toggle(tab))}
                aria-current={tab === activeTab ? "page" : undefined}
              >
                {tab}
              </Button>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}

const SharesCategoryTabs = (props) => {
  const {
    disabledRightsEditor,
    disableTabs,
    isInChangesModule = false,
    authorizations = [],
    status,
    error,
    isReviewMode,
  } = props;

  const role = Cookies.get("role");

  const dispatch = useDispatch();
  const params = useParams();
  const currentUser = useSelector((state) => state.userSlice);
  const sharesFromState = useSelector((state) => state.sharesTableSlice.shares);
  const { activeTab } = useSelector((state) => state.sharesTableSlice);
  const { company } = useSelector((state) => state.companySlice);
  const { selectedTemplate, getTemplateForApplicant, showOverlay } =
    useSelector((state) => state.templateSlice);

  const ApprovedAuthorizations = useSelector((state) =>
    (state.shareAuthorizationSlice?.shares || []).filter(
      (auth) => auth.isApproved === true
    )
  );
  const hasApprovedAuthorizations = ApprovedAuthorizations.length > 0;
  const approvedCategories = _uniq(
    ApprovedAuthorizations.map((auth) => auth.sharesCategory).filter(Boolean)
  );

  // State to control authorizations table and approved categories
  const [showAuthorizationsTable, setShowAuthorizationsTable] = useState(false);
  const [showApprovedCategories, setShowApprovedCategories] = useState(false);

  // Determine shares based on props or state
  const shares = props.nominalShares || sharesFromState;

  // Determine activeShares
  const activeShares = isInChangesModule
    ? shares.filter((share) =>
        showApprovedCategories
          ? approvedCategories.includes(share.sharesCategory)
          : !approvedCategories.includes(share.sharesCategory)
      )
    : shares.filter((share) => share.sharesNumber > 0);

  // Derive unique categories from activeShares
  const categories = _uniq(
    activeShares.map((row) => row.sharesCategory).filter(Boolean)
  );

  const companyId = company?._id ?? params.companyId;
  const selectedShare = activeShares.find(
    (share) => share.sharesCategory === activeTab
  );
  const templateVersions = selectedTemplate?.versions
    ? selectedTemplate.versions
    : [];
  const latestTemplateVersion =
    templateVersions?.[templateVersions.length - 1] ?? {};

  useEffect(() => {
    dispatch(
      updateTemplateState({
        selectedTemplate: selectedShare?.rightsAttached,
      })
    );
  }, [selectedShare, dispatch]);

  const toggle = (tab) => {
    if (activeTab !== tab) {
      dispatch(updateNominalSharesSlice({ activeTab: tab }));
      setShowAuthorizationsTable(false); // Ensure table is hidden to show rights
    } else {
      dispatch(updateNominalSharesSlice({ activeTab: "" }));
      setShowAuthorizationsTable(false); // Hide table when deselecting
    }
  };

  const handleAuthorizationToggle = () => {
    if (!showAuthorizationsTable) {
      dispatch(updateNominalSharesSlice({ activeTab: "" })); // Clear activeTab
      setShowApprovedCategories(true); // Show approved categories
    } else {
      setShowApprovedCategories(false); // Hide approved categories
    }
    setShowAuthorizationsTable(!showAuthorizationsTable); // Toggle table visibility
  };

  useEffect(() => {
    if (!selectedShare?._id && activeTab) {
      dispatch(updateNominalSharesSlice({ activeTab: "" }));
      setShowAuthorizationsTable(false); // Hide table if no valid share is selected
    }
  }, [activeShares, selectedShare, activeTab, dispatch]);

  return (
    <div className="shadow sm:rounded-md sm:overflow-hidden relative">
      <div className="px-4 py-6 space-y-6 bg-white sm:p-6">
        <div className="w-full flex justify-between align-baseline">
          <ShareRights
            tabs={categories}
            toggle={toggle}
            activeTab={activeTab}
            disabled={disableTabs}
          />
          {hasApprovedAuthorizations && (
            <button
              className={classNames(
                showAuthorizationsTable
                  ? "border-sflPrimary text-sflPrimary"
                  : "border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-300",
                "border rounded-full p-1 px-2 font-medium text-sm"
              )}
              onClick={handleAuthorizationToggle}
            >
              Existing Authorization
            </button>
          )}
        </div>

        <div className="outlet">
          {getTemplateForApplicant.status === "loading" || showOverlay ? (
            <div className="flex justify-center mx-auto mt-10 text-white">
              <SpinnerIcon className="text-gray-400" />
            </div>
          ) : (
            <>
              {showAuthorizationsTable ? (
                <ExistingAuthorizationsTable
                  authorizations={authorizations}
                  status={status}
                  error={error}
                />
              ) : !selectedTemplate?._id && activeTab ? (
                <p className="text-center font-medium">
                  No respective rights attached
                </p>
              ) : (
                <>
                  {activeTab === Constants.ORDINARY_SHARES && (
                    <OnlyOfficeDocumentEditor
                      key={activeTab}
                      userFullName={getFullName(currentUser.names)}
                      title={activeTab}
                      mode={"view"}
                      userId={currentUser._id}
                      companyId={companyId}
                      url={
                        latestTemplateVersion?.fileUploads?.template
                          ? `${process.env.REACT_APP_IMAGE_BASE_URL}/templates/${latestTemplateVersion.fileUploads.template}`
                          : null
                      }
                      editorKey={`${selectedTemplate?._id}-${latestTemplateVersion?._id}`}
                      containerHeight="850px"
                    />
                  )}
                  {activeTab === Constants.PREFERENCE_SHARES && (
                    <OnlyOfficeDocumentEditor
                      key={activeTab}
                      userFullName={getFullName(currentUser.names)}
                      mode={"edit"}
                      title={activeTab}
                      userId={currentUser._id}
                      companyId={companyId}
                      url={
                        latestTemplateVersion?.fileUploads?.template
                          ? `${process.env.REACT_APP_IMAGE_BASE_URL}/templates/${latestTemplateVersion.fileUploads.template}`
                          : null
                      }
                      editorKey={`${selectedTemplate?._id}-${latestTemplateVersion?._id}`}
                      containerHeight="900px"
                      permissions={{
                        review:
                          role === Constants.COOKIE_ROLES.OFFICIAL
                            ? false
                            : true,
                        comment: true,
                        edit:
                          role === Constants.COOKIE_ROLES.OFFICIAL
                            ? false
                            : isReviewMode
                            ? false
                            : true, //Handle editing mode
                        editCommentAuthorOnly: isReviewMode ? true : false,
                        deleteCommentAuthorOnly: isReviewMode ? true : false,
                      }}
                      metadata={{
                        isFromIncorporation: "isFromIncorporation",
                        fileName: latestTemplateVersion.fileUploads.template,
                      }}
                    />
                  )}
                  {activeTab &&
                    ![
                      Constants.ORDINARY_SHARES,
                      Constants.PREFERENCE_SHARES,
                    ].includes(activeTab) && (
                      <OnlyOfficeDocumentEditor
                        key={activeTab}
                        userFullName={getFullName(currentUser.names)}
                        mode={"edit"}
                        userId={currentUser._id}
                        title={activeTab}
                        companyId={companyId}
                        url={
                          latestTemplateVersion?.fileUploads?.template
                            ? `${process.env.REACT_APP_IMAGE_BASE_URL}/templates/${latestTemplateVersion.fileUploads.template}`
                            : null
                        }
                        editorKey={`${selectedTemplate?._id}-${latestTemplateVersion?._id}`}
                        containerHeight="900px"
                        permissions={{
                          review:
                            role === Constants.COOKIE_ROLES.OFFICIAL
                              ? false
                              : true,
                          comment: true,
                          edit:
                            role === Constants.COOKIE_ROLES.OFFICIAL
                              ? false
                              : isReviewMode
                              ? false
                              : true, //Handle editing mode
                          editCommentAuthorOnly: isReviewMode ? true : false,
                          deleteCommentAuthorOnly: isReviewMode ? true : false,
                        }}
                        metadata={{
                          isFromIncorporation: "isFromIncorporation",
                          fileName: latestTemplateVersion.fileUploads.template,
                        }}
                      />
                    )}
                  {!activeTab && (
                    <p className="text-center font-medium">
                      Select tab to open and close respective rights
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharesCategoryTabs;
